import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { statusColor, formatDate } from '../../utils/format.js'
import {
  getScreeningCampaign,
  getScreeningCohortReport,
  listScreeningRecords,
  startScreeningEncounter,
  updateScreeningRecord,
  updateScreeningCampaign,
  deleteScreeningCampaign,
  deleteScreeningRecord,
  generateFitnessReport,
  listDoctorsForScreening,
} from '../../api/screening.js'
import { listOrgStaffLinks } from '../../api/organizations.js'

const FITNESS_OPTIONS = [
  ['', 'Not recorded'],
  ['fit', 'Fit for work'],
  ['fit_with_restrictions', 'Fit with restrictions'],
  ['unfit', 'Unfit for work'],
  ['refer', 'Refer for further assessment'],
]

/**
 * /superuser/screening/campaigns/:id — one campaign: header, the
 * PHI-safe cohort report, and the list of people screened with their
 * fitness-for-work outcome and a Fitness for Work PDF button.
 */
export default function ScreeningCampaignDetail() {
  const { id } = useParams()
  const [editing, setEditing] = useState(false)
  const campaignQ = useQuery({
    queryKey: ['screening-campaign', id],
    queryFn: () => getScreeningCampaign(id),
    enabled: Boolean(id),
  })
  const campaign = campaignQ.data

  return (
    <div className="space-y-4">
      <Link to="/superuser/screening"
        className="text-xs text-wantok-700 hover:underline">← All screening</Link>

      {campaignQ.error && (
        <ErrorBanner error={campaignQ.error} onRetry={() => campaignQ.refetch()} />
      )}
      {campaignQ.isLoading && (
        <Card><div className="text-sm text-slate-400">Loading campaign…</div></Card>
      )}

      {campaign && (
        <>
          <Card>
            <CardHeader
              title={campaign.name}
              subtitle={`${campaign.organization_name} · ${campaign.protocol_name}`}
              right={
                <div className="flex items-center gap-2">
                  <span className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-medium ${statusColor(campaign.status)}`}>
                    {campaign.status_display || campaign.status}
                  </span>
                  <button onClick={() => setEditing(true)}
                    className="text-xs font-semibold tracking-wide px-3 py-1.5 rounded-full border border-sand-300 text-slate-600 hover:bg-sand-50 transition-colors">
                    Edit
                  </button>
                </div>
              }
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1">
              <Meta label="Delivery">{deliveryLabel(campaign.delivery_mode)}</Meta>
              <Meta label="Scheduled">
                {campaign.scheduled_start ? formatDate(campaign.scheduled_start) : '—'}
                {campaign.scheduled_end ? ` – ${formatDate(campaign.scheduled_end)}` : ''}
              </Meta>
              <Meta label="Expected">{campaign.expected_headcount || '—'}</Meta>
              <Meta label="Location">{campaign.location || '—'}</Meta>
            </div>
          </Card>

          <CohortReportCard campaignId={id} />
          <RecordsCard campaign={campaign} />
          {editing && (
            <CampaignEditModal campaign={campaign} onClose={() => setEditing(false)} />
          )}
        </>
      )}
    </div>
  )
}

// ===========================================================================
// Cohort report
// ===========================================================================

function CohortReportCard({ campaignId }) {
  const q = useQuery({
    queryKey: ['screening-cohort', campaignId],
    queryFn: () => getScreeningCohortReport(campaignId),
    enabled: Boolean(campaignId),
  })
  const r = q.data

  return (
    <Card>
      <CardHeader
        title="Cohort report"
        subtitle="Aggregate findings across everyone screened. Counts and distributions only — no individual clinical detail."
      />
      {q.error && <ErrorBanner error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading && <div className="text-sm text-slate-400 py-2">Loading…</div>}
      {r && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Screened" value={r.participation?.screened ?? 0} />
            <Stat label="Expected" value={r.participation?.expected ?? 0} />
            <Stat label="Participation"
              value={r.participation?.percent != null ? `${r.participation.percent}%` : '—'} />
            <Stat label="High risk" value={r.high_risk_count ?? 0}
              tone={r.high_risk_count ? 'rose' : 'slate'} />
          </div>
          <Distribution title="Fitness for work" data={r.fitness_breakdown}
            labels={{
              fit: 'Fit', fit_with_restrictions: 'Fit with restrictions',
              unfit: 'Unfit', refer: 'Refer', not_recorded: 'Not recorded',
            }} />
          <Distribution title="Blood pressure" data={r.blood_pressure}
            labels={{
              normal: 'Normal', elevated: 'Elevated',
              hypertensive: 'Hypertensive', not_measured: 'Not measured',
            }} />
          <Distribution title="BMI" data={r.bmi}
            labels={{
              underweight: 'Underweight', normal: 'Normal',
              overweight: 'Overweight', obese: 'Obese', not_measured: 'Not measured',
            }} />
          {r.top_diagnoses?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                Top findings
              </div>
              <table className="w-full text-xs">
                <tbody className="divide-y divide-slate-100">
                  {r.top_diagnoses.map((d, i) => (
                    <tr key={i}>
                      <td className="py-1 text-slate-700">{d.finding}</td>
                      <td className="py-1 text-right tabular-nums text-slate-500 w-12">
                        {d.count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

function Distribution({ title, data, labels }) {
  if (!data) return null
  const entries = Object.entries(labels).filter(([k]) => k in data)
  const total = entries.reduce((s, [k]) => s + (data[k] || 0), 0)
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{title}</div>
      <div className="space-y-1">
        {entries.map(([k, lbl]) => {
          const v = data[k] || 0
          const pct = total > 0 ? (v / total) * 100 : 0
          return (
            <div key={k} className="flex items-center gap-2 text-xs">
              <div className="w-40 text-slate-600">{lbl}</div>
              <div className="flex-1 bg-sand-100 rounded-full h-2 overflow-hidden">
                <div className="bg-wantok-500 h-full" style={{ width: `${pct}%` }} />
              </div>
              <div className="w-8 text-right tabular-nums text-slate-600">{v}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ===========================================================================
// People screened
// ===========================================================================

function RecordsCard({ campaign }) {
  const [showScreen, setShowScreen] = useState(false)
  const [fitnessFor, setFitnessFor] = useState(null)
  const recordsQ = useQuery({
    queryKey: ['screening-records', campaign.id],
    queryFn: () => listScreeningRecords({ campaign: campaign.id }),
    enabled: Boolean(campaign.id),
  })
  const records = recordsQ.data || []

  return (
    <Card>
      <CardHeader
        title="People screened"
        subtitle="Everyone screened into this campaign and their fitness-for-work outcome."
        right={
          <button onClick={() => setShowScreen(true)}
            className="text-xs font-semibold tracking-wide px-3 py-1.5 rounded-full bg-wantok-600 text-white hover:bg-wantok-700 transition-colors">
            + Screen a patient
          </button>
        }
      />
      {recordsQ.error && (
        <ErrorBanner error={recordsQ.error} onRetry={() => recordsQ.refetch()} />
      )}
      {recordsQ.isLoading && <div className="text-sm text-slate-400 py-3">Loading…</div>}
      {recordsQ.data && (
        records.length === 0 ? (
          <div className="text-sm text-slate-400 italic py-6 text-center">
            No one screened yet. Use “+ Screen a patient” to start.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <th className="text-left p-2">Patient</th>
                  <th className="text-left p-2">Conducting doctor</th>
                  <th className="text-left p-2 w-24">Encounter</th>
                  <th className="text-left p-2 w-44">Fitness outcome</th>
                  <th className="p-2 w-48"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {records.map((rec) => (
                  <RecordRow key={rec.id} record={rec}
                    onFitness={() => setFitnessFor(rec)} />
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
      {showScreen && (
        <ScreenPatientModal campaign={campaign} onClose={() => setShowScreen(false)} />
      )}
      {fitnessFor && (
        <FitnessModal record={fitnessFor} onClose={() => setFitnessFor(null)} />
      )}
    </Card>
  )
}

function RecordRow({ record, onFitness }) {
  const qc = useQueryClient()
  const genMut = useMutation({
    mutationFn: () => generateFitnessReport(record.encounter, {}),
  })
  const removeMut = useMutation({
    mutationFn: () => deleteScreeningRecord(record.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['screening-records', record.campaign] })
      qc.invalidateQueries({ queryKey: ['screening-cohort', String(record.campaign)] })
    },
  })
  const fitnessLabel =
    (FITNESS_OPTIONS.find(([v]) => v === record.fitness_outcome) || [])[1]
    || 'Not recorded'

  return (
    <tr className="hover:bg-sand-50/50">
      <td className="p-2 text-slate-800">{record.patient_name}</td>
      <td className="p-2 text-slate-600">{record.doctor_name || '—'}</td>
      <td className="p-2 text-slate-500 text-xs">{record.encounter_status}</td>
      <td className="p-2">
        <span className={record.fitness_outcome ? 'text-slate-700' : 'text-slate-400 italic'}>
          {fitnessLabel}
        </span>
      </td>
      <td className="p-2 text-right space-x-2 whitespace-nowrap">
        <button onClick={onFitness}
          className="text-xs text-wantok-700 hover:underline">Record fitness</button>
        <button onClick={() => genMut.mutate()} disabled={genMut.isPending}
          className="text-xs text-wantok-700 hover:underline disabled:opacity-50">
          {genMut.isPending ? 'Generating…' : 'Fitness PDF'}
        </button>
        {genMut.isSuccess && genMut.data?.file && (
          <a href={genMut.data.file} target="_blank" rel="noreferrer"
            className="text-xs text-emerald-700 hover:underline">Open</a>
        )}
        {genMut.isError && (
          <span className="text-xs text-rose-600" title={String(genMut.error?.message || '')}>
            failed
          </span>
        )}
        <button
          onClick={() => {
            if (window.confirm(`Remove ${record.patient_name} from this campaign? The clinical encounter is kept.`)) {
              removeMut.mutate()
            }
          }}
          disabled={removeMut.isPending}
          className="text-xs text-rose-600 hover:underline disabled:opacity-50"
        >
          {removeMut.isPending ? 'Removing…' : 'Remove'}
        </button>
      </td>
    </tr>
  )
}

function ScreenPatientModal({ campaign, onClose }) {
  const qc = useQueryClient()
  const staffQ = useQuery({
    queryKey: ['screening-org-staff', campaign.organization],
    queryFn: () => listOrgStaffLinks({ orgId: campaign.organization, isActive: true }),
    enabled: Boolean(campaign.organization),
  })
  const doctorsQ = useQuery({
    queryKey: ['screening-doctors'],
    queryFn: () => listDoctorsForScreening(),
    staleTime: 5 * 60 * 1000,
  })
  const staff = staffQ.data || []
  const doctors = doctorsQ.data || []

  const [patientId, setPatientId] = useState('')
  const [doctorId, setDoctorId] = useState('')

  const mut = useMutation({
    mutationFn: () => startScreeningEncounter(campaign.id, {
      patient_id: parseInt(patientId, 10),
      doctor_id: parseInt(doctorId, 10),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['screening-records', campaign.id] })
      qc.invalidateQueries({ queryKey: ['screening-cohort', String(campaign.id)] })
      qc.invalidateQueries({ queryKey: ['screening-campaign', String(campaign.id)] })
      onClose()
    },
  })

  return (
    <Modal title="Screen a patient" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); mut.mutate() }} className="space-y-3">
        <p className="text-xs text-slate-500">
          Creates a clinical encounter for the chosen person and links it to this
          campaign. The conducting doctor completes the encounter in the usual
          clinical notes editor.
        </p>
        <Field label="Patient (from the organisation's roster)">
          <select value={patientId} required
            onChange={(e) => setPatientId(e.target.value)} className={inputCls}>
            <option value="">Select a patient…</option>
            {staff.map((s) => (
              <option key={s.id} value={s.patient}>
                {s.patient_name || `Patient #${s.patient}`}
                {s.employee_id ? ` (${s.employee_id})` : ''}
              </option>
            ))}
          </select>
          {staff.length === 0 && !staffQ.isLoading && (
            <div className="text-[11px] text-amber-700 mt-1">
              This organisation has no enrolled members — import a roster on the org
              dashboard first.
            </div>
          )}
        </Field>
        <Field label="Conducting doctor">
          <select value={doctorId} required
            onChange={(e) => setDoctorId(e.target.value)} className={inputCls}>
            <option value="">Select a doctor…</option>
            {doctors.map((d) => (
              <option key={d.id} value={d.id}>{doctorLabel(d)}</option>
            ))}
          </select>
        </Field>
        {mut.error && <FormError error={mut.error} />}
        <ModalActions onClose={onClose} submitting={mut.isPending}
          submitLabel="Screen patient" disabled={!patientId || !doctorId} />
      </form>
    </Modal>
  )
}

function FitnessModal({ record, onClose }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    fitness_outcome: record.fitness_outcome || '',
    fitness_restrictions: record.fitness_restrictions || '',
    fitness_valid_until: record.fitness_valid_until || '',
  })
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const mut = useMutation({
    mutationFn: () => updateScreeningRecord(record.id, {
      fitness_outcome: form.fitness_outcome,
      fitness_restrictions: form.fitness_restrictions,
      fitness_valid_until: form.fitness_valid_until || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['screening-records', record.campaign] })
      qc.invalidateQueries({ queryKey: ['screening-cohort', String(record.campaign)] })
      onClose()
    },
  })

  return (
    <Modal title={`Fitness outcome — ${record.patient_name}`} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); mut.mutate() }} className="space-y-3">
        <Field label="Outcome">
          <select value={form.fitness_outcome}
            onChange={(e) => setF('fitness_outcome', e.target.value)}
            className={inputCls}>
            {FITNESS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
        <Field label="Restrictions / accommodations">
          <textarea rows={2} value={form.fitness_restrictions}
            onChange={(e) => setF('fitness_restrictions', e.target.value)}
            placeholder="Only when the outcome is fit with restrictions"
            className={inputCls} />
        </Field>
        <Field label="Valid until (optional)">
          <input type="date" value={form.fitness_valid_until}
            onChange={(e) => setF('fitness_valid_until', e.target.value)}
            className={inputCls} />
        </Field>
        <p className="text-[11px] text-slate-500">
          The Fitness for Work PDF pulls this outcome automatically.
        </p>
        {mut.error && <FormError error={mut.error} />}
        <ModalActions onClose={onClose} submitting={mut.isPending}
          submitLabel="Save outcome" />
      </form>
    </Modal>
  )
}

function CampaignEditModal({ campaign, onClose }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [form, setForm] = useState({
    name: campaign.name || '',
    status: campaign.status || 'planned',
    delivery_mode: campaign.delivery_mode || 'site_visit',
    scheduled_start: campaign.scheduled_start || '',
    scheduled_end: campaign.scheduled_end || '',
    location: campaign.location || '',
    expected_headcount: campaign.expected_headcount ?? '',
  })
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const saveMut = useMutation({
    mutationFn: () => updateScreeningCampaign(campaign.id, {
      name: form.name,
      status: form.status,
      delivery_mode: form.delivery_mode,
      scheduled_start: form.scheduled_start || null,
      scheduled_end: form.scheduled_end || null,
      location: form.location,
      expected_headcount: form.expected_headcount === ''
        ? 0 : parseInt(form.expected_headcount, 10),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['screening-campaign', String(campaign.id)] })
      qc.invalidateQueries({ queryKey: ['screening-campaigns'] })
      onClose()
    },
  })

  const deleteMut = useMutation({
    mutationFn: () => deleteScreeningCampaign(campaign.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['screening-campaigns'] })
      navigate('/superuser/screening')
    },
  })

  return (
    <Modal title="Edit campaign" onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); saveMut.mutate() }} className="space-y-3">
        <Field label="Campaign name">
          <input type="text" required value={form.name}
            onChange={(e) => setF('name', e.target.value)} className={inputCls} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Status">
            <select value={form.status} onChange={(e) => setF('status', e.target.value)}
              className={inputCls}>
              <option value="planned">Planned</option>
              <option value="in_progress">In progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </Field>
          <Field label="Delivery mode">
            <select value={form.delivery_mode}
              onChange={(e) => setF('delivery_mode', e.target.value)} className={inputCls}>
              <option value="site_visit">Site visit</option>
              <option value="clinic">Clinic</option>
              <option value="telehealth">Telehealth</option>
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Scheduled start">
            <input type="date" value={form.scheduled_start}
              onChange={(e) => setF('scheduled_start', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Scheduled end">
            <input type="date" value={form.scheduled_end}
              onChange={(e) => setF('scheduled_end', e.target.value)} className={inputCls} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Expected headcount">
            <input type="number" min="0" value={form.expected_headcount}
              onChange={(e) => setF('expected_headcount', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Location">
            <input type="text" value={form.location}
              onChange={(e) => setF('location', e.target.value)} className={inputCls} />
          </Field>
        </div>
        {(saveMut.error || deleteMut.error) && (
          <FormError error={saveMut.error || deleteMut.error} />
        )}
        <div className="flex items-center justify-between pt-2">
          <button type="button"
            onClick={() => {
              if (window.confirm(`Delete campaign “${campaign.name}”? This removes its screening records — the clinical encounters are kept.`)) {
                deleteMut.mutate()
              }
            }}
            disabled={deleteMut.isPending}
            className="text-xs text-rose-600 hover:underline disabled:opacity-50"
          >
            {deleteMut.isPending ? 'Deleting…' : 'Delete campaign'}
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={saveMut.isPending}
              className="px-3 py-1.5 rounded-md border border-sand-300 text-sm text-slate-600 hover:bg-sand-50">
              Cancel
            </button>
            <button type="submit" disabled={saveMut.isPending || !form.name.trim()}
              className="px-3 py-1.5 rounded-md bg-wantok-600 text-white text-sm hover:bg-wantok-700 disabled:opacity-50">
              {saveMut.isPending ? 'Saving…' : 'Save campaign'}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}

// ===========================================================================
// Shared building blocks
// ===========================================================================

const inputCls = 'w-full border border-sand-300 rounded-md px-2 py-1.5 text-sm bg-white'

function deliveryLabel(mode) {
  return ({ site_visit: 'Site visit', clinic: 'Clinic', telehealth: 'Telehealth' })[mode]
    || mode || '—'
}

function doctorLabel(d) {
  return d.full_name || d.name || d.display_name
    || (d.user && (d.user.full_name || d.user.username))
    || `Doctor #${d.id}`
}

function Meta({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-sm text-slate-700">{children}</div>
    </div>
  )
}

function Stat({ label, value, tone = 'slate' }) {
  const tones = { slate: 'text-slate-800', rose: 'text-rose-700' }
  return (
    <div className="border border-sand-200 rounded-lg px-3 py-2 text-center">
      <div className={`text-xl font-semibold ${tones[tone] || tones.slate}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  )
}

function FormError({ error }) {
  const msg = error?.response?.data?.error?.message
    || error?.response?.data?.detail
    || error?.response?.data?.record && 'This person is already enrolled in the campaign.'
    || error?.message
    || 'Something went wrong.'
  return (
    <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
      {String(msg)}
    </div>
  )
}

function ModalActions({ onClose, submitting, submitLabel, disabled }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onClose} disabled={submitting}
        className="px-3 py-1.5 rounded-md border border-sand-300 text-sm text-slate-600 hover:bg-sand-50">
        Cancel
      </button>
      <button type="submit" disabled={submitting || disabled}
        className="px-3 py-1.5 rounded-md bg-wantok-600 text-white text-sm hover:bg-wantok-700 disabled:opacity-50">
        {submitting ? 'Saving…' : submitLabel}
      </button>
    </div>
  )
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-sand-200 w-full max-w-lg mt-12 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl leading-none"
            aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  )
}
